import { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import FilterPanel from "../components/FilterPanel";
import ThermoTable, { SortState } from "../components/ThermoTable";
import Gallery from "../components/Gallery";
import DetailModal from "../components/DetailModal";
import CenterLineChart from "../components/visualizations/CenterLineChart";
import StatsPanel from "../components/visualizations/StatsPanel";
import AnalysisPanel from "../components/AnalysisPanel";
import NasLoginModal from "../components/NasLoginModal";
import ThermoHeightWizardModal from "../components/ThermoHeightWizardModal";
import { useEscapeStack } from "../hooks/useEscapeStack";
import {
  clearCache,
  createThermoImage,
  deleteThermoImage,
  detectThermoAnomalies,
  fetchCenterLine,
  fetchProductionStepMapping,
  fetchThermoFileIndex,
  fetchThermoMetadata,
  fetchVftSettingsForTimestamp,
  insertThermoData,
  loadAnalyses,
  updateAnalysis
} from "../api/postgrest";
import {
  SavedAnalysis,
  ThermoDataRow,
  ThermoDetail,
  ThermoImage,
  ThermoSamplePayload,
  VftSettings
} from "../types";
import { ThermoFilters, TrialSelections, applyFilters, defaultFilters } from "../utils/filtering";
import { summarizeDetails } from "../utils/aggregation";
import { buildProductionStepConfigFromMapping, matchProductionStep } from "../utils/prodStepMatching";
import {
  buildSampleIdSignature,
  heightCalibrationFromSavedAnalysis,
  ThermoHeightCalibration
} from "../utils/thermoHeightProjection";
import { useNas } from "../context/NasContext";
import { getPostgrestUrl } from "../settings";
import { isShowMode } from "../showMode";
import {
  formatAppDateTime,
  formatCurrentDateTime,
  formatDateFilterDisplay
} from "../utils/dateTime";

const PAGE_SIZE = 12;
const NAS_THERMO_INPUT_DIR = "thermoimaging";
const NAS_THERMO_OUTPUT_DIR = "thermoimaging/output";
const THERMO_OUTPUT_KEYS = [
  "temp_image",
  "visc_image",
  "temp_cut_image",
  "visc_cut_image",
  "center_line_plot"
];
const THERMO_OUTPUT_FILES = [
  "temp_image.png",
  "viscosity_image.png",
  "temp_cut_image.png",
  "viscosity_cut_image.png",
  "center_line.png"
];
const SYNC_PAUSE_POLL_MS = 250;
const SHOW_FILTER_SELECTION_PATH = "./db/filter_selection.json";

type LogTone = "info" | "success" | "warn" | "error" | "prod_step";

type LogEntry = {
  message: string;
  tone: LogTone;
};

type SyncProgressState = {
  foldersTotal: number;
  foldersDone: number;
  currentFolder: string;
  filesTotal: number;
  filesDone: number;
  totalFiles: number;
  totalFilesDone: number;
};

type ShowFilterSelection = {
  thermo_analysis_ids?: number[];
};

const buildInitialSyncProgress = (): SyncProgressState => ({
  foldersTotal: 0,
  foldersDone: 0,
  currentFolder: "",
  filesTotal: 0,
  filesDone: 0,
  totalFiles: 0,
  totalFilesDone: 0
});

const LOG_TONE_COLORS: Record<LogTone, string> = {
  info: "var(--text-muted)",
  success: "var(--status-ok)",
  warn: "#f7d774",
  error: "var(--status-error)",
  prod_step: "#c084fc"
};

function joinNasPath(...parts: Array<string | undefined>) {
  const cleaned = parts
    .filter(Boolean)
    .map((part, index) => {
      const value = String(part);
      if (index === 0) return value.replace(/[\\/]+$/, "");
      return value.replace(/^[\\/]+|[\\/]+$/g, "");
    });
  return cleaned.join("/");
}

const KNOWN_TRIAL_TYPES = ["Glaschemie", "Brennstoff", "Energieverhältnis", "Formgebung"];

function createEmptyTrialSelections(): TrialSelections {
  return {
    Glaschemie: [],
    Brennstoff: [],
    "Energieverhältnis": [],
    Formgebung: []
  };
}

function describeUnique(values: Array<string | null | undefined>) {
  const unique = Array.from(
    new Set(values.map((value) => String(value || "").trim()).filter(Boolean))
  ).sort();
  if (unique.length === 0) return "All";
  if (unique.length === 1) return unique[0];
  return "Mixed";
}

function datePart(value: string | null | undefined) {
  const text = String(value || "").trim();
  return text ? text.slice(0, 10) : "";
}

function describeDateRange(values: Array<string | null | undefined>) {
  const unique = Array.from(
    new Set(values.map((value) => datePart(value)).filter(Boolean))
  ).sort();
  if (unique.length === 0) return "All";
  if (unique.length === 1) return unique[0];
  return `${unique[0]} to ${unique[unique.length - 1]}`;
}

function collectTrialSelections(rows: ThermoImage[]): TrialSelections {
  const map = new Map<string, Set<string>>();
  for (const row of rows) {
    for (const trial of row.active_trials || []) {
      if (!KNOWN_TRIAL_TYPES.includes(trial.trial_type)) continue;
      const set = map.get(trial.trial_type) || new Set<string>();
      set.add(trial.descriptive_name);
      map.set(trial.trial_type, set);
    }
  }
  const selections = createEmptyTrialSelections();
  for (const [trialType, values] of map) {
    selections[trialType] = Array.from(values).sort();
  }
  return selections;
}

function buildShowThermoFilters(rows: ThermoImage[]): ThermoFilters {
  return {
    date: null,
    calibration: null,
    prodStep: null,
    trials: collectTrialSelections(rows)
  };
}

function buildShowThermoFilterLabels(rows: ThermoImage[]) {
  return {
    date: describeDateRange(rows.map((row) => row.timestamp)),
    calibration: describeUnique(rows.map((row) => row.cal_name)),
    prodStep: describeUnique(rows.map((row) => row.prod_step))
  };
}

function parseBoolean(value: unknown) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") return value.trim().toLowerCase() === "true";
  return false;
}

export default function ThermoimagingPage() {
  const { status: nasStatus, mountPoint, error: nasError, config, setConfig, triggerMount } = useNas();

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<ThermoImage[]>([]);
  const [filtered, setFiltered] = useState<ThermoImage[]>([]);
  const [filters, setFilters] = useState<ThermoFilters>(defaultFilters);
  const [draftFilters, setDraftFilters] = useState<ThermoFilters>(defaultFilters);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadedAnalysisFilterIds, setLoadedAnalysisFilterIds] = useState<Set<number> | null>(null);
  const [sort, setSort] = useState<SortState>({ field: "timestamp", direction: "desc" });
  const [page, setPage] = useState(0);
  const [galleryPage, setGalleryPage] = useState(0);
  const [anomalyGalleryPage, setAnomalyGalleryPage] = useState(0);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [analyzedIds, setAnalyzedIds] = useState<Set<number>>(new Set());
  const [details, setDetails] = useState<Record<number, ThermoDetail>>({});
  const [showAllowedThermoAnalysisIds, setShowAllowedThermoAnalysisIds] = useState<Set<number>>(new Set());
  const [showPresets, setShowPresets] = useState<SavedAnalysis[]>([]);
  const [showSelectedPresetId, setShowSelectedPresetId] = useState<number | null>(null);
  const [showLoadedPresetId, setShowLoadedPresetId] = useState<number | null>(null);
  const [showPresetStatus, setShowPresetStatus] = useState<string | null>(null);
  const [showPresetLoading, setShowPresetLoading] = useState(false);
  const [showPresetHydrating, setShowPresetHydrating] = useState(false);
  const [currentLoadedAnalysis, setCurrentLoadedAnalysis] = useState<SavedAnalysis | null>(null);
  const [heightCalibration, setHeightCalibration] = useState<ThermoHeightCalibration | null>(null);
  const [heightWizardOpen, setHeightWizardOpen] = useState(false);
  const [analysisRefreshKey, setAnalysisRefreshKey] = useState(0);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailModalImage, setDetailModalImage] = useState<ThermoImage | null>(null);
  const [detailModalLoading, setDetailModalLoading] = useState(false);
  const [detailModalError, setDetailModalError] = useState<string | null>(null);
  const [anomalyIds, setAnomalyIds] = useState<Set<number>>(new Set());
  const [anomalyLoading, setAnomalyLoading] = useState(false);
  const [anomalyError, setAnomalyError] = useState<string | null>(null);
  const [anomalyDurationMs, setAnomalyDurationMs] = useState<number | null>(null);
  const anomalyRunRef = useRef(0);
  const anomalyStartRef = useRef<number | null>(null);
  const [, forceTick] = useState(0);
  const [prodStepWarningOpen, setProdStepWarningOpen] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncRunning, setSyncRunning] = useState(false);
  const [syncLogs, setSyncLogs] = useState<LogEntry[]>([]);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncDryRun, setSyncDryRun] = useState(false);
  const lastDryRunDirRef = useRef<string | null>(null);
  const [syncFolderOptions, setSyncFolderOptions] = useState<string[]>([]);
  const [syncFolderFilter, setSyncFolderFilter] = useState("");
  const [syncFolderLoading, setSyncFolderLoading] = useState(false);
  const [syncPaused, setSyncPaused] = useState(false);
  const [syncStopRequested, setSyncStopRequested] = useState(false);
  const syncControlRef = useRef({ pause: false, stop: false });
  const [syncProgress, setSyncProgress] = useState<SyncProgressState>(buildInitialSyncProgress);
  const syncLogSuppressedRef = useRef(false);

  const [nasCreds, setNasCreds] = useState(config);
  const needsLogin = !isShowMode && nasStatus !== "mounted";

  const downloadBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const downloadText = (content: string, fileName: string, type: string) => {
    const blob = new Blob([content], { type });
    downloadBlob(blob, fileName);
  };

  const buildExportFileName = (prefix: string, extension: string) => {
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    return `${prefix}_${stamp}.${extension}`;
  };

  const escapeHtml = (value: string) =>
    value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const formatNumber = (value: number, digits: number) =>
    Number.isFinite(value) ? value.toFixed(digits) : "n/a";

  const renderTable = (headers: string[], rows: string[][]) => {
    const head = headers.map((header) => `<th>${escapeHtml(header)}</th>`).join("");
    const body = rows
      .map(
        (cells) =>
          `<tr>${cells.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`
      )
      .join("");
    return `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
  };

  useEffect(() => {
    if (isShowMode || nasStatus === "mounted") {
      loadMetadata();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nasStatus, isShowMode]);

  const loadMetadata = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchThermoMetadata();
      setMetadata(data);
    } catch (err) {
      console.error(err);
      setLoadError(err instanceof Error ? err.message : "Failed to load metadata");
    } finally {
      setLoading(false);
    }
  };

  const loadShowPresets = async () => {
    setShowPresetLoading(true);
    setShowPresetStatus(null);
    try {
      const configResp = await fetch(SHOW_FILTER_SELECTION_PATH, {
        cache: "no-store"
      });
      if (!configResp.ok) {
        throw new Error(
          `Show preset config not found (${SHOW_FILTER_SELECTION_PATH}). HTTP ${configResp.status}`
        );
      }

      const config = (await configResp.json()) as ShowFilterSelection;
      const allowedPresetIds = new Set(
        Array.isArray(config.thermo_analysis_ids)
          ? config.thermo_analysis_ids
              .map((value) => Number(value))
              .filter((value) => Number.isFinite(value) && value > 0)
          : []
      );

      if (!allowedPresetIds.size) {
        throw new Error(
          "Show preset config has no thermo_analysis_ids. Publish profile must define at least one preset."
        );
      }

      const allPresets = await loadAnalyses();
      const filteredPresets = allPresets.filter((analysis) => allowedPresetIds.has(analysis.id));
      if (!filteredPresets.length) {
        throw new Error(
          "No matching thermo presets found in database for thermo_analysis_ids from show config."
        );
      }

      setShowAllowedThermoAnalysisIds(allowedPresetIds);
      setShowPresets(filteredPresets);
      setShowSelectedPresetId((prev) => {
        if (prev != null && filteredPresets.some((entry) => entry.id === prev)) return prev;
        return filteredPresets[0]?.id ?? null;
      });
      setShowLoadedPresetId((prev) => {
        if (prev != null && filteredPresets.some((entry) => entry.id === prev)) return prev;
        return null;
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load show presets.";
      setShowPresetStatus(message);
      setShowAllowedThermoAnalysisIds(new Set());
      setShowPresets([]);
      setShowSelectedPresetId(null);
      setShowLoadedPresetId(null);
    } finally {
      setShowPresetLoading(false);
    }
  };

  useEffect(() => {
    if (!isShowMode) {
      setShowAllowedThermoAnalysisIds(new Set());
      setShowPresets([]);
      setShowSelectedPresetId(null);
      setShowLoadedPresetId(null);
      setShowPresetStatus(null);
      setShowPresetLoading(false);
      setShowPresetHydrating(false);
      return;
    }
    void loadShowPresets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowMode]);

  const restoreAnalysisFilters: ThermoFilters = {
    date: null,
    calibration: null,
    prodStep: null,
    trials: createEmptyTrialSelections()
  };

  useEffect(() => {
    const next = loadedAnalysisFilterIds
      ? metadata.filter((img) => loadedAnalysisFilterIds.has(img.id))
      : applyFilters(metadata, filters, searchTerm);
    const sorted = [...next].sort((a, b) => {
      const direction = sort.direction === "asc" ? 1 : -1;
      if (sort.field === "timestamp") {
        return (a.timestamp > b.timestamp ? 1 : -1) * direction;
      }
      if (sort.field === "prod_step") {
        return (a.prod_step > b.prod_step ? 1 : -1) * direction;
      }
      return (a.cal_name > b.cal_name ? 1 : -1) * direction;
    });
    setFiltered(sorted);
    setPage(0);
  }, [loadedAnalysisFilterIds, metadata, filters, sort, searchTerm]);

  useEffect(() => {
    if (isShowMode) return;
    const ids = Array.from(analyzedIds);
    const missing = ids.filter((id) => !details[id]);
    if (!missing.length) return;
    setDetailLoading(true);
    Promise.all(
      missing.map((id) => {
        const epsilon = metadata.find((row) => row.id === id)?.epsilon;
        return fetchCenterLine(id, epsilon);
      })
    )
      .then((newDetails) => {
        setDetails((prev) => {
          const next = { ...prev };
          newDetails.forEach((detail) => {
            next[detail.id] = detail;
          });
          return next;
        });
      })
      .finally(() => setDetailLoading(false));
  }, [analyzedIds, details, isShowMode, metadata]);

  useEffect(() => {
    if (!detailModalImage) {
      setDetailModalLoading(false);
      setDetailModalError(null);
      return;
    }
    if (details[detailModalImage.id]) {
      setDetailModalLoading(false);
      setDetailModalError(null);
      return;
    }

    let cancelled = false;
    setDetailModalLoading(true);
    setDetailModalError(null);
    fetchCenterLine(detailModalImage.id, detailModalImage.epsilon)
      .then((detail) => {
        if (cancelled) return;
        setDetails((prev) => ({
          ...prev,
          [detail.id]: detail
        }));
      })
      .catch((err) => {
        if (cancelled) return;
        setDetailModalError(err instanceof Error ? err.message : "Centerline load failed.");
      })
      .finally(() => {
        if (!cancelled) setDetailModalLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [detailModalImage, details]);

  const prodSteps = useMemo(
    () => Array.from(new Set(metadata.map((m) => m.prod_step))).sort(),
    [metadata]
  );
  const metadataById = useMemo(() => new Map(metadata.map((row) => [row.id, row])), [metadata]);
  const fetchCenterLineForImage = (id: number) =>
    fetchCenterLine(id, metadataById.get(id)?.epsilon);
  const analysisMatchesSelectedIds = (analysis: SavedAnalysis | null, ids: Iterable<number>) =>
    !!analysis &&
    buildSampleIdSignature(analysis.sample_id_array) === buildSampleIdSignature(ids);

  const clearHeightProjectionState = () => {
    setCurrentLoadedAnalysis(null);
    setHeightCalibration(null);
  };

  const trialOptions = useMemo(() => {
    const knownTypes = ["Glaschemie", "Brennstoff", "Energieverhältnis", "Formgebung"];
    const base: Record<string, string[]> = {
      Glaschemie: [],
      Brennstoff: [],
      Energieverhältnis: [],
      Formgebung: []
    };
    const map = new Map<string, Set<string>>();
    metadata.forEach((img) => {
      (img.active_trials || []).forEach((trial) => {
        if (!knownTypes.includes(trial.trial_type)) return;
        const set = map.get(trial.trial_type) || new Set<string>();
        set.add(trial.descriptive_name);
        map.set(trial.trial_type, set);
      });
    });
    map.forEach((value, key) => {
      base[key] = Array.from(value.values()).sort();
    });
    return base;
  }, [metadata]);

  const analyzedImages = useMemo(
    () => filtered.filter((img) => analyzedIds.has(img.id)),
    [filtered, analyzedIds]
  );
  const showTrialOptions = useMemo(
    () => collectTrialSelections(analyzedImages),
    [analyzedImages]
  );
  const activeShowPreset = useMemo(() => {
    if (!isShowMode || showLoadedPresetId == null) return null;
    return showPresets.find((preset) => preset.id === showLoadedPresetId) || null;
  }, [isShowMode, showLoadedPresetId, showPresets]);
  const showHasLoadedPreset = !isShowMode || showLoadedPresetId != null;
  const showPresetBusy = showPresetLoading || showPresetHydrating;
  const showReadOnlyFilterLabels = useMemo(
    () => buildShowThermoFilterLabels(analyzedImages),
    [analyzedImages]
  );
  const analyzedDetails = analyzedImages
    .map((img) => details[img.id])
    .filter(Boolean) as ThermoDetail[];
  const analyzedIdSignature = useMemo(() => buildSampleIdSignature(analyzedIds), [analyzedIds]);
  const analyzedDetailSignature = useMemo(
    () => buildSampleIdSignature(analyzedDetails.map((detail) => detail.id)),
    [analyzedDetails]
  );
  const activeHeightCalibration = useMemo(() => {
    if (!heightCalibration) return null;
    const calibrationSignature = buildSampleIdSignature(heightCalibration.sampleIds);
    return calibrationSignature === analyzedIdSignature && calibrationSignature === analyzedDetailSignature
      ? heightCalibration
      : null;
  }, [analyzedDetailSignature, analyzedIdSignature, heightCalibration]);
  const currentHeightReference = useMemo(() => {
    if (!heightCalibration) return null;
    return buildSampleIdSignature(heightCalibration.sampleIds) === analyzedIdSignature
      ? heightCalibration
      : null;
  }, [analyzedIdSignature, heightCalibration]);
  const analyzedVft = analyzedImages.find((img) => img.vftCoeffs)?.vftCoeffs || null;
  const anomalyImages = useMemo(
    () => analyzedImages.filter((img) => anomalyIds.has(img.id)),
    [analyzedImages, anomalyIds]
  );
  const nonAnomalyImages = useMemo(
    () => analyzedImages.filter((img) => !anomalyIds.has(img.id)),
    [analyzedImages, anomalyIds]
  );
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(nonAnomalyImages.length / 8));
    if (galleryPage >= totalPages) {
      setGalleryPage(totalPages - 1);
    }
  }, [analyzedImages, nonAnomalyImages, galleryPage]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(anomalyImages.length / 8));
    if (anomalyGalleryPage >= totalPages) {
      setAnomalyGalleryPage(totalPages - 1);
    }
  }, [anomalyImages, anomalyGalleryPage]);

  const aggregated = useMemo(() => {
    if (isShowMode && activeShowPreset) {
      const presetStats = {
        minTemperature: activeShowPreset.min_temperature,
        meanTemperature: activeShowPreset.mean_temperature,
        maxTemperature: activeShowPreset.max_temperature,
        minViscosity: activeShowPreset.min_viscosity,
        meanViscosity: activeShowPreset.mean_viscosity,
        maxViscosity: activeShowPreset.max_viscosity
      };
      return {
        centerLine: Array.isArray(activeShowPreset.mean_center_lines)
          ? activeShowPreset.mean_center_lines
          : [],
        stats: presetStats,
        bottleStats: {
          ...presetStats,
          count: analyzedImages.length
        }
      };
    }
    return summarizeDetails(analyzedDetails, activeHeightCalibration);
  }, [activeHeightCalibration, activeShowPreset, analyzedDetails, analyzedImages.length, isShowMode]);
  const usesProjectedHeight = aggregated.centerLine.some((point) => Number.isFinite(point.height_mm));
  const canOpenHeightWizard =
    analyzedIds.size > 0 &&
    analyzedImages.length === analyzedIds.size &&
    analyzedDetails.length === analyzedIds.size &&
    !detailLoading;

  const handleSelectToggle = (id: number) => {
    if (isShowMode) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setGalleryPage(0);
  };

  const handleRemoveFromAnalysis = (id: number) => {
    setHeightWizardOpen(false);
    clearHeightProjectionState();
    setAnalyzedIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setAnomalyIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setSelectedIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setGalleryPage(0);
    setAnomalyGalleryPage(0);
  };

  const handleSelectAll = (ids: number[]) => {
    if (isShowMode) return;
    setSelectedIds(new Set(ids));
    setGalleryPage(0);
  };

  const handleApplyFilters = () => {
    setLoadedAnalysisFilterIds(null);
    setFilters(draftFilters);
  };

  const handleClearFilters = () => {
    setLoadedAnalysisFilterIds(null);
    setDraftFilters(defaultFilters);
    setFilters(defaultFilters);
    setSearchTerm("");
  };

  const handleRestoreAnalysis = (analysis: SavedAnalysis) => {
    const restoredIds = analysis.sample_id_array
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
    const restoredSet = new Set(restoredIds);
    const restoredCalibration = heightCalibrationFromSavedAnalysis(analysis);

    setLoadedAnalysisFilterIds(restoredSet);
    setDraftFilters(restoreAnalysisFilters);
    setFilters(restoreAnalysisFilters);
    setSearchTerm("");
    setPage(0);
    setGalleryPage(0);
    setAnomalyGalleryPage(0);
    setSelectedIds(restoredSet);
    setAnalyzedIds(restoredSet);
    setHeightWizardOpen(false);
    setAnomalyIds(new Set());
    setAnomalyError(null);
    setAnomalyLoading(false);
    setAnomalyDurationMs(null);
    anomalyStartRef.current = null;
    anomalyRunRef.current += 1;
    setProdStepWarningOpen(false);
    setCurrentLoadedAnalysis(analysis);
    setHeightCalibration(restoredCalibration);
  };

  const handleLoadShowPreset = async (presetId: number | null) => {
    setAnomalyError(null);
    setShowPresetStatus(null);
    if (presetId == null) {
      handleResetAll();
      setShowLoadedPresetId(null);
      return;
    }

    const preset = showPresets.find((entry) => entry.id === presetId);
    if (!preset) {
      setShowPresetStatus("Selected preset is not available in this published set.");
      return;
    }

    const ids = preset.sample_id_array
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
    const rows = ids.map((id) => metadataById.get(id)).filter(Boolean) as ThermoImage[];
    if (rows.length !== ids.length) {
      setShowPresetStatus("The selected preset is missing sample metadata in this published set.");
      return;
    }

    setShowPresetHydrating(true);
    try {
      await Promise.resolve();
      const readonlyFilters = buildShowThermoFilters(rows);
      const idSet = new Set(ids);
      const restoredCalibration = heightCalibrationFromSavedAnalysis(preset);
      setLoadedAnalysisFilterIds(idSet);
      setDraftFilters(readonlyFilters);
      setFilters(readonlyFilters);
      setSearchTerm("");
      setPage(0);
      setGalleryPage(0);
      setAnomalyGalleryPage(0);
      setSelectedIds(idSet);
      setAnalyzedIds(idSet);
      setHeightWizardOpen(false);
      setAnomalyIds(new Set());
      setAnomalyLoading(false);
      setAnomalyDurationMs(null);
      anomalyStartRef.current = null;
      anomalyRunRef.current += 1;
      setProdStepWarningOpen(false);
      setCurrentLoadedAnalysis(preset);
      setHeightCalibration(restoredCalibration);
      setShowLoadedPresetId(preset.id);
    } catch (err) {
      setShowPresetStatus(err instanceof Error ? err.message : "Failed to load selected preset.");
    } finally {
      setShowPresetHydrating(false);
    }
  };

  const detailForModal = detailModalImage ? details[detailModalImage.id] || null : null;

  const handleResetAll = () => {
    setLoadedAnalysisFilterIds(null);
    setDraftFilters(defaultFilters);
    setFilters(defaultFilters);
    setSearchTerm("");
    setSort({ field: "timestamp", direction: "desc" });
    setPage(0);
    setGalleryPage(0);
    setAnomalyGalleryPage(0);
    setSelectedIds(new Set());
    setAnalyzedIds(new Set());
    setAnomalyIds(new Set());
    setAnomalyError(null);
    setAnomalyLoading(false);
    setAnomalyDurationMs(null);
    anomalyStartRef.current = null;
    anomalyRunRef.current += 1;
    setProdStepWarningOpen(false);
    setHeightWizardOpen(false);
    clearHeightProjectionState();
    setShowSelectedPresetId(isShowMode ? showPresets[0]?.id ?? null : null);
    setShowLoadedPresetId(null);
  };

  const handleSearchChange = (value: string) => {
    setLoadedAnalysisFilterIds(null);
    setSearchTerm(value);
  };

  const classifyLogLine = (line: string): LogTone => {
    const lower = line.toLowerCase();
    if (lower.includes("detected prod_step")) return "prod_step";
    if (lower.includes("fail") || lower.includes("error")) return "error";
    if (
      lower.includes("missing") ||
      lower.includes("skipping") ||
      lower.includes("no vft") ||
      lower.includes("no csv") ||
      lower.includes("no folders") ||
      lower.includes("stopping") ||
      lower.includes("stopped")
    ) {
      return "warn";
    }
    if (
      lower.includes("ok") ||
      lower.includes("complete") ||
      lower.includes("saved outputs") ||
      lower.includes("processing completed")
    ) {
      return "success";
    }
    return "info";
  };

  const appendSyncLog = (message: string, reset = false, tone?: LogTone, force = false) => {
    if (syncLogSuppressedRef.current && !force) return;
    const timestamp = formatCurrentDateTime();
    const formatted = String(message ?? "").replace(/\\n/g, "\n");
    const lines = formatted.split(/\n/);
    setSyncLogs((prev) => {
      const base = reset ? [] : prev;
      const next = lines.map((line) => ({
        message: `[${timestamp}] ${line}`,
        tone: tone || classifyLogLine(line)
      }));
      return [...base, ...next].slice(-500);
    });
  };

  const clampPercent = (value: number) => Math.max(0, Math.min(100, value));

  const normalizeSamplePayload = (
    raw: Record<string, unknown>,
    folderName: string,
    fileName: string
  ): ThermoSamplePayload => {
    const timestamp = typeof raw.timestamp === "string" ? raw.timestamp : "";
    if (!timestamp) {
      throw new Error(`Missing timestamp in sample metadata for ${fileName}`);
    }
    return {
      timestamp,
      cal_name: typeof raw.cal_name === "string" ? raw.cal_name : "Unknown",
      epsilon: Number(raw.epsilon ?? 0),
      ms: Number(raw.ms ?? 0),
      integ_time: Number(raw.integ_time ?? 0),
      frame_index: Number(raw.frame_index ?? 0),
      calib: typeof raw.calib === "string" ? raw.calib : "Unknown",
      cam_temp: Number(raw.cam_temp ?? 0),
      prod_step: typeof raw.prod_step === "string" ? raw.prod_step : "Unknown",
      height: Number(raw.height ?? 0),
      filename: fileName,
      folder_name: folderName
    };
  };

  const normalizeDataRows = (rows: Record<string, unknown>[]): ThermoDataRow[] =>
    rows
      .map((row) => ({
        x: Number(row.x),
        y: Number(row.y),
        temperature: Number(row.temperature),
        viscosity: Number(row.viscosity),
        bottle: parseBoolean(row.bottle),
        center_line: parseBoolean(row.center_line)
      }))
      .filter(
        (row) =>
          Number.isFinite(row.x) &&
          Number.isFinite(row.y) &&
          Number.isFinite(row.temperature) &&
          Number.isFinite(row.viscosity)
      );

  const validateVftTimestamp = (sampleTimestamp: string, vftTimestamp?: string) => {
    const sampleTime = Date.parse(sampleTimestamp);
    const vftTime = vftTimestamp ? Date.parse(vftTimestamp) : Number.NaN;
    const sampleLabel = formatAppDateTime(sampleTimestamp, sampleTimestamp);
    const vftLabel = vftTimestamp
      ? formatAppDateTime(vftTimestamp, vftTimestamp)
      : "n/a";
    if (!Number.isFinite(sampleTime) || !Number.isFinite(vftTime)) {
      appendSyncLog(
        `VFT check: unable to parse timestamps (sample=${sampleLabel}, vft=${vftLabel})`,
        false,
        "error"
      );
      return;
    }
    const ok = vftTime <= sampleTime;
    appendSyncLog(
      `VFT check: ${ok ? "OK" : "FAIL"} (vft=${vftLabel} <= sample=${sampleLabel})`,
      false,
      ok ? "success" : "error"
    );
  };

  const validateOutputFiles = async (outputDir: string) => {
    if (!window.api?.listNasDir) return;
    const listing = await window.api.listNasDir(outputDir);
    if (!listing.ok || !listing.entries) {
      appendSyncLog(
        `Output check: unable to list ${outputDir} (${listing.message || "unknown error"})`,
        false,
        "error"
      );
      return;
    }
    const found = new Set(listing.entries.filter((entry) => entry.isFile).map((entry) => entry.name));
    const missing = THERMO_OUTPUT_FILES.filter((file) => !found.has(file));
    if (missing.length) {
      appendSyncLog(`Output check: missing ${missing.join(", ")}`, false, "error");
    } else {
      appendSyncLog("Output check: all expected files present.", false, "success");
    }
  };

  const safeDeleteSample = async (id: number, reason: string) => {
    try {
      await deleteThermoImage(id);
      appendSyncLog(`Deleted sample ${id} (${reason}).`);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Delete failed";
      appendSyncLog(`Failed to delete sample ${id}: ${message}`);
    }
  };

  const deleteDryRunDir = async (dirPath: string, reason: string) => {
    if (!window.api?.deletePath) {
      appendSyncLog("Dry run cleanup requires the desktop runtime.");
      return;
    }
    const resp = await window.api.deletePath(dirPath);
    if (!resp.ok) {
      appendSyncLog(
        `Dry run cleanup failed for ${dirPath}: ${resp.message || "unknown error"}`
      );
      return;
    }
    appendSyncLog(`Dry run cleanup: removed ${dirPath} (${reason}).`);
  };

  const waitForSyncReady = async () => {
    if (syncControlRef.current.stop) return false;
    while (syncControlRef.current.pause) {
      if (syncControlRef.current.stop) return false;
      await new Promise((resolve) => setTimeout(resolve, SYNC_PAUSE_POLL_MS));
    }
    return !syncControlRef.current.stop;
  };

  useEffect(() => {
    // Selection changed; keep previous analysis visible but clear stale errors.
    setAnomalyError(null);
  }, [selectedIds]);

  useEffect(() => {
    if (!anomalyLoading || !anomalyStartRef.current) return;
    const interval = setInterval(() => {
      forceTick((n) => n + 1);
    }, 200);
    return () => clearInterval(interval);
  }, [anomalyLoading]);

  const handleAnalyze = async (skipWarning = false) => {
    if (anomalyLoading) return;
    if (isShowMode) {
      if (showSelectedPresetId == null) {
        setAnomalyError("Select a preset, then click Analyze.");
        return;
      }
      const preset = showPresets.find((entry) => entry.id === showSelectedPresetId);
      if (!preset) {
        setAnomalyError("Selected preset could not be loaded.");
        return;
      }
      const idsToAnalyze = new Set(preset.sample_id_array || []);
      setSelectedIds(new Set(idsToAnalyze));
      setAnalyzedIds(new Set(idsToAnalyze));
      setAnomalyIds(new Set());
      setAnomalyLoading(false);
      setAnomalyError(null);
      setAnomalyDurationMs(null);
      anomalyStartRef.current = null;
      setGalleryPage(0);
      setAnomalyGalleryPage(0);
      return;
    }
    if (selectedIds.size === 0) {
      setAnalyzedIds(new Set());
      setAnomalyIds(new Set());
      setAnomalyError("Select at least one sample, then click Analyze.");
      return;
    }

    // Guard: warn if multiple production steps are selected
    const selectedSteps = new Set(
      metadata.filter((m) => selectedIds.has(m.id)).map((m) => m.prod_step)
    );
    if (selectedSteps.size > 1 && !skipWarning) {
      setProdStepWarningOpen(true);
      return;
    }

    const idsToAnalyze = new Set(selectedIds);
    setHeightWizardOpen(false);
    if (!analysisMatchesSelectedIds(currentLoadedAnalysis, idsToAnalyze)) {
      clearHeightProjectionState();
    }
    setAnalyzedIds(idsToAnalyze);
    setGalleryPage(0);
    setAnomalyGalleryPage(0);
    setAnomalyIds(new Set());
    setAnomalyError(null);
    setAnomalyDurationMs(null);

    const runId = anomalyRunRef.current + 1;
    anomalyRunRef.current = runId;
    if (idsToAnalyze.size <= 1) {
      setAnomalyLoading(false);
      setAnomalyIds(new Set());
      setAnomalyError(null);
      anomalyStartRef.current = null;
      return;
    }

    const startedAt = Date.now();
    anomalyStartRef.current = startedAt;
    setAnomalyLoading(true);
    setAnomalyError(null);
    try {
      const result = await detectThermoAnomalies(Array.from(idsToAnalyze));
      if (anomalyRunRef.current !== runId) return;
      const flagged = Object.entries(result)
        .filter(([, isAnomaly]) => isAnomaly)
        .map(([id]) => Number(id))
        .filter((id) => !Number.isNaN(id));
      setAnomalyIds(new Set(flagged));
    } catch (err) {
      if (anomalyRunRef.current !== runId) return;
      console.error(err);
      setAnomalyIds(new Set());
      setAnomalyError(err instanceof Error ? err.message : "Anomaly detection failed");
    } finally {
      if (anomalyRunRef.current !== runId) return;
      const endedAt = Date.now();
      if (anomalyStartRef.current) {
        setAnomalyDurationMs(endedAt - anomalyStartRef.current);
      }
      anomalyStartRef.current = null;
      setAnomalyLoading(false);
    }
  };

  const summarizeWithProjectedHeight = (detailList: ThermoDetail[]) => {
    const detailIdsSignature = buildSampleIdSignature(detailList.map((detail) => detail.id));
    const calibration =
      activeHeightCalibration &&
      buildSampleIdSignature(activeHeightCalibration.sampleIds) === detailIdsSignature
        ? activeHeightCalibration
        : null;
    return summarizeDetails(detailList, calibration);
  };

  const handleApplyHeightCalibration = async (calibration: ThermoHeightCalibration) => {
    const detailList = analyzedImages
      .map((image) => details[image.id])
      .filter(Boolean) as ThermoDetail[];
    if (detailList.length !== analyzedIds.size) {
      throw new Error("Center line details are still loading. Wait a moment and try again.");
    }

    const nextAggregated = summarizeDetails(detailList, calibration);
    if (!nextAggregated.centerLine.length) {
      throw new Error("Unable to build a projected center line for this reference.");
    }

    const nextReferenceFields = {
      height_reference_sample_id: calibration.referenceSampleId,
      height_reference_line: calibration.referenceLine,
      height_reference_height_mm: calibration.referenceHeightMm
    } as const;

    const loadedAnalysis = currentLoadedAnalysis;
    if (loadedAnalysis && analysisMatchesSelectedIds(loadedAnalysis, calibration.sampleIds)) {
      await updateAnalysis(loadedAnalysis.id, {
        mean_center_lines: nextAggregated.centerLine,
        ...nextReferenceFields
      });
      clearCache();
      setAnalysisRefreshKey((value) => value + 1);
      setCurrentLoadedAnalysis((prev) =>
        prev
          ? {
              ...prev,
              mean_center_lines: nextAggregated.centerLine,
              ...nextReferenceFields
            }
          : prev
      );
    }

    setHeightCalibration(calibration);
    setHeightWizardOpen(false);
  };

  const prepareCenterLineExport = async () => {
    if (!analyzedImages.length) {
      window.alert("Analyze at least one sample before exporting Center Line stats.");
      return null;
    }

    const includePerSampleStats = window.confirm(
      "Include per-sample Center Line stats in the export?"
    );

    const exportedAtLocal = formatCurrentDateTime();
    const analyzedIdList = analyzedImages.map((img) => img.id);
    const allAnalyzedIds = Array.from(analyzedIds);
    const missingIds = analyzedIdList.filter((id) => !details[id]);
    let fetchedDetails: ThermoDetail[] = [];

    if (missingIds.length) {
      setDetailLoading(true);
      try {
        fetchedDetails = await Promise.all(missingIds.map((id) => fetchCenterLineForImage(id)));
        if (fetchedDetails.length) {
          setDetails((prev) => {
            const next = { ...prev };
            fetchedDetails.forEach((detail) => {
              next[detail.id] = detail;
            });
            return next;
          });
        }
      } catch (err) {
        console.error("Failed to fetch missing details for export.", err);
      } finally {
        setDetailLoading(false);
      }
    }

    const detailMap: Record<number, ThermoDetail> = { ...details };
    fetchedDetails.forEach((detail) => {
      detailMap[detail.id] = detail;
    });

    const stillMissingIds = analyzedIdList.filter((id) => !detailMap[id]);
    const detailList = analyzedIdList
      .map((id) => detailMap[id])
      .filter(Boolean) as ThermoDetail[];
    const exportAggregated = summarizeWithProjectedHeight(detailList).stats;
    const totalCenterLinePoints = detailList.reduce(
      (sum, detail) => sum + (detail.centerLine?.length ?? 0),
      0
    );

    const trialSummary = Object.entries(filters.trials)
      .filter(([, values]) => values.length)
      .map(([type, values]) => `${type}: ${values.join(", ")}`);

    const filtersSummary: Array<[string, string]> = [
      ["Date", formatDateFilterDisplay(filters.date)],
      ["Calibration", filters.calibration || "All"],
      ["Production Step", filters.prodStep || "All"],
      ["Trials", trialSummary.length ? trialSummary.join("; ") : "All"],
      ["Search", searchTerm || "All"],
      ["Sort", `${sort.field} ${sort.direction}`]
    ];

    const selectionSummary: Array<[string, string]> = [
      ["Exported At", exportedAtLocal],
      ["Basis", "Filtered analyzed"],
      ["Analyzed (filtered)", `${analyzedImages.length}`],
      ["Analyzed (all)", `${allAnalyzedIds.length}`],
      ["Selected", `${selectedIds.size}`],
      [
        "Missing detail IDs",
        stillMissingIds.length ? stillMissingIds.join(", ") : "None"
      ],
      ["Per-sample stats included", includePerSampleStats ? "Yes" : "No"]
    ];

    const aggregatedRows: Array<[string, string]> = [
      ["Min Temp (C)", formatNumber(exportAggregated.minTemperature, 2)],
      ["Mean Temp (C)", formatNumber(exportAggregated.meanTemperature, 2)],
      ["Max Temp (C)", formatNumber(exportAggregated.maxTemperature, 2)],
      ["Min Viscosity log10(eta / dPa*s)", formatNumber(exportAggregated.minViscosity, 4)],
      ["Mean Viscosity log10(eta / dPa*s)", formatNumber(exportAggregated.meanViscosity, 4)],
      ["Max Viscosity log10(eta / dPa*s)", formatNumber(exportAggregated.maxViscosity, 4)],
      ["Total Center Line Points", `${totalCenterLinePoints}`]
    ];

    const sampleHeaders = [
      "ID",
      "Timestamp",
      "Calibration",
      "Production Step",
      "Folder",
      "Filename",
      "Height"
    ];
    if (includePerSampleStats) {
      sampleHeaders.push(
        "Min Temp (C)",
        "Mean Temp (C)",
        "Max Temp (C)",
        "Min Visc log10(eta / dPa*s)",
        "Mean Visc log10(eta / dPa*s)",
        "Max Visc log10(eta / dPa*s)",
        "Center Line Points"
      );
    }

    const sampleRows = analyzedImages.map((img) => {
      const cells = [
        `${img.id}`,
        formatAppDateTime(img.timestamp),
        img.cal_name,
        img.prod_step,
        img.folder_name,
        img.filename,
        formatNumber(img.height, 2)
      ];
      if (includePerSampleStats) {
        const stats = detailMap[img.id]?.stats;
        const pointCount = detailMap[img.id]?.centerLine?.length;
        cells.push(
          stats ? formatNumber(stats.minTemperature, 2) : "n/a",
          stats ? formatNumber(stats.meanTemperature, 2) : "n/a",
          stats ? formatNumber(stats.maxTemperature, 2) : "n/a",
          stats ? formatNumber(stats.minViscosity, 4) : "n/a",
          stats ? formatNumber(stats.meanViscosity, 4) : "n/a",
          stats ? formatNumber(stats.maxViscosity, 4) : "n/a",
          Number.isFinite(pointCount) ? `${pointCount}` : "n/a"
        );
      }
      return cells;
    });

    return {
      includePerSampleStats,
      exportedAtLocal,
      filtersSummary,
      selectionSummary,
      aggregatedRows,
      sampleHeaders,
      sampleRows
    };
  };

  const prepareHotAreaExport = async () => {
    if (!analyzedImages.length) {
      window.alert("Analyze at least one sample before exporting Hot Area stats.");
      return null;
    }

    const includePerSampleStats = window.confirm(
      "Include per-sample Hot Area | Gob | Bottle stats in the export?"
    );

    const exportedAtLocal = formatCurrentDateTime();
    const analyzedIdList = analyzedImages.map((img) => img.id);
    const allAnalyzedIds = Array.from(analyzedIds);
    const missingIds = analyzedIdList.filter((id) => !details[id]);
    let fetchedDetails: ThermoDetail[] = [];

    if (missingIds.length) {
      setDetailLoading(true);
      try {
        fetchedDetails = await Promise.all(missingIds.map((id) => fetchCenterLineForImage(id)));
        if (fetchedDetails.length) {
          setDetails((prev) => {
            const next = { ...prev };
            fetchedDetails.forEach((detail) => {
              next[detail.id] = detail;
            });
            return next;
          });
        }
      } catch (err) {
        console.error("Failed to fetch missing details for export.", err);
      } finally {
        setDetailLoading(false);
      }
    }

    const detailMap: Record<number, ThermoDetail> = { ...details };
    fetchedDetails.forEach((detail) => {
      detailMap[detail.id] = detail;
    });

    const stillMissingIds = analyzedIdList.filter((id) => !detailMap[id]);
    const detailList = analyzedIdList
      .map((id) => detailMap[id])
      .filter(Boolean) as ThermoDetail[];
    const exportAggregated = summarizeWithProjectedHeight(detailList).bottleStats;
    const totalBottlePoints = detailList.reduce(
      (sum, detail) => sum + (detail.bottleStats?.count ?? 0),
      0
    );

    const trialSummary = Object.entries(filters.trials)
      .filter(([, values]) => values.length)
      .map(([type, values]) => `${type}: ${values.join(", ")}`);

    const filtersSummary: Array<[string, string]> = [
      ["Date", formatDateFilterDisplay(filters.date)],
      ["Calibration", filters.calibration || "All"],
      ["Production Step", filters.prodStep || "All"],
      ["Trials", trialSummary.length ? trialSummary.join("; ") : "All"],
      ["Search", searchTerm || "All"],
      ["Sort", `${sort.field} ${sort.direction}`]
    ];

    const selectionSummary: Array<[string, string]> = [
      ["Exported At", exportedAtLocal],
      ["Basis", "Filtered analyzed"],
      ["Analyzed (filtered)", `${analyzedImages.length}`],
      ["Analyzed (all)", `${allAnalyzedIds.length}`],
      ["Selected", `${selectedIds.size}`],
      [
        "Missing detail IDs",
        stillMissingIds.length ? stillMissingIds.join(", ") : "None"
      ],
      ["Per-sample stats included", includePerSampleStats ? "Yes" : "No"]
    ];

    const aggregatedRows: Array<[string, string]> = [
      ["Min Temp (C)", formatNumber(exportAggregated.minTemperature, 2)],
      ["Mean Temp (C)", formatNumber(exportAggregated.meanTemperature, 2)],
      ["Max Temp (C)", formatNumber(exportAggregated.maxTemperature, 2)],
      ["Min Viscosity log10(eta / dPa*s)", formatNumber(exportAggregated.minViscosity, 4)],
      ["Mean Viscosity log10(eta / dPa*s)", formatNumber(exportAggregated.meanViscosity, 4)],
      ["Max Viscosity log10(eta / dPa*s)", formatNumber(exportAggregated.maxViscosity, 4)],
      ["Total Bottle Points", `${totalBottlePoints}`]
    ];

    const sampleHeaders = [
      "ID",
      "Timestamp",
      "Calibration",
      "Production Step",
      "Folder",
      "Filename",
      "Height"
    ];
    if (includePerSampleStats) {
      sampleHeaders.push(
        "Min Temp (C)",
        "Mean Temp (C)",
        "Max Temp (C)",
        "Min Visc log10(eta / dPa*s)",
        "Mean Visc log10(eta / dPa*s)",
        "Max Visc log10(eta / dPa*s)",
        "Count"
      );
    }

    const sampleRows = analyzedImages.map((img) => {
      const cells = [
        `${img.id}`,
        formatAppDateTime(img.timestamp),
        img.cal_name,
        img.prod_step,
        img.folder_name,
        img.filename,
        formatNumber(img.height, 2)
      ];
      if (includePerSampleStats) {
        const stats = detailMap[img.id]?.bottleStats;
        cells.push(
          stats ? formatNumber(stats.minTemperature, 2) : "n/a",
          stats ? formatNumber(stats.meanTemperature, 2) : "n/a",
          stats ? formatNumber(stats.maxTemperature, 2) : "n/a",
          stats ? formatNumber(stats.minViscosity, 4) : "n/a",
          stats ? formatNumber(stats.meanViscosity, 4) : "n/a",
          stats ? formatNumber(stats.maxViscosity, 4) : "n/a",
          stats ? `${stats.count}` : "n/a"
        );
      }
      return cells;
    });

    return {
      includePerSampleStats,
      exportedAtLocal,
      filtersSummary,
      selectionSummary,
      aggregatedRows,
      sampleHeaders,
      sampleRows
    };
  };

  const handleExportCenterLineHtml = async () => {
    const exportData = await prepareCenterLineExport();
    if (!exportData) return;

    const exportHtml = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Thermoimaging Center Line Export</title>
    <style>
      body { font-family: Arial, sans-serif; margin: 24px; color: #0b1220; }
      h1 { margin-bottom: 4px; }
      h2 { margin-top: 24px; margin-bottom: 8px; }
      p.meta { color: #5b667a; margin-top: 0; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
      th, td { border: 1px solid #d4d9e2; padding: 8px 10px; font-size: 13px; text-align: left; }
      th { background: #f3f6fb; }
      .note { font-size: 12px; color: #6b7280; }
    </style>
  </head>
  <body>
    <h1>Thermoimaging Center Line</h1>
    <p class="meta">Exported at ${escapeHtml(exportData.exportedAtLocal)}</p>
    <h2>Filters</h2>
    ${renderTable(
      ["Filter", "Value"],
      exportData.filtersSummary.map(([label, value]) => [label, value])
    )}
    <h2>Selection Summary</h2>
    ${renderTable(
      ["Item", "Value"],
      exportData.selectionSummary.map(([label, value]) => [label, value])
    )}
    <h2>Aggregated Stats</h2>
    ${renderTable(
      ["Metric", "Value"],
      exportData.aggregatedRows.map(([label, value]) => [label, value])
    )}
    <h2>Samples</h2>
    ${renderTable(exportData.sampleHeaders, exportData.sampleRows)}
    <p class="note">Per-sample stats included: ${
      exportData.includePerSampleStats ? "Yes" : "No"
    }</p>
  </body>
</html>`;

    const fileName = buildExportFileName("thermoimaging_center_line_export", "html");
    downloadText(exportHtml, fileName, "text/html");
  };

  const handleExportBottleStatsHtml = async () => {
    const exportData = await prepareHotAreaExport();
    if (!exportData) return;

    const exportHtml = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Thermoimaging Hot Area Export</title>
    <style>
      body { font-family: Arial, sans-serif; margin: 24px; color: #0b1220; }
      h1 { margin-bottom: 4px; }
      h2 { margin-top: 24px; margin-bottom: 8px; }
      p.meta { color: #5b667a; margin-top: 0; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
      th, td { border: 1px solid #d4d9e2; padding: 8px 10px; font-size: 13px; text-align: left; }
      th { background: #f3f6fb; }
      .note { font-size: 12px; color: #6b7280; }
    </style>
  </head>
  <body>
    <h1>Thermoimaging Hot Area | Gob | Bottle</h1>
    <p class="meta">Exported at ${escapeHtml(exportData.exportedAtLocal)}</p>
    <h2>Filters</h2>
    ${renderTable(
      ["Filter", "Value"],
      exportData.filtersSummary.map(([label, value]) => [label, value])
    )}
    <h2>Selection Summary</h2>
    ${renderTable(
      ["Item", "Value"],
      exportData.selectionSummary.map(([label, value]) => [label, value])
    )}
    <h2>Aggregated Stats</h2>
    ${renderTable(
      ["Metric", "Value"],
      exportData.aggregatedRows.map(([label, value]) => [label, value])
    )}
    <h2>Samples</h2>
    ${renderTable(exportData.sampleHeaders, exportData.sampleRows)}
    <p class="note">Per-sample stats included: ${
      exportData.includePerSampleStats ? "Yes" : "No"
    }</p>
  </body>
</html>`;

    const fileName = buildExportFileName("thermoimaging_hot_area_export", "html");
    downloadText(exportHtml, fileName, "text/html");
  };

  const handleExportCenterLineXlsx = async () => {
    const exportData = await prepareCenterLineExport();
    if (!exportData) return;

    const workbook = XLSX.utils.book_new();
    const filtersSheet = XLSX.utils.aoa_to_sheet([
      ["Filter", "Value"],
      ...exportData.filtersSummary
    ]);
    const selectionSheet = XLSX.utils.aoa_to_sheet([
      ["Item", "Value"],
      ...exportData.selectionSummary
    ]);
    const aggregatedSheet = XLSX.utils.aoa_to_sheet([
      ["Metric", "Value"],
      ...exportData.aggregatedRows
    ]);
    const samplesSheet = XLSX.utils.aoa_to_sheet([
      exportData.sampleHeaders,
      ...exportData.sampleRows
    ]);

    XLSX.utils.book_append_sheet(workbook, filtersSheet, "Filters");
    XLSX.utils.book_append_sheet(workbook, selectionSheet, "Selection");
    XLSX.utils.book_append_sheet(workbook, aggregatedSheet, "Aggregated Stats");
    XLSX.utils.book_append_sheet(workbook, samplesSheet, "Samples");

    const workbookData = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([workbookData], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
    const fileName = buildExportFileName("thermoimaging_center_line_export", "xlsx");
    downloadBlob(blob, fileName);
  };

  const handleExportBottleStatsXlsx = async () => {
    const exportData = await prepareHotAreaExport();
    if (!exportData) return;

    const workbook = XLSX.utils.book_new();
    const filtersSheet = XLSX.utils.aoa_to_sheet([
      ["Filter", "Value"],
      ...exportData.filtersSummary
    ]);
    const selectionSheet = XLSX.utils.aoa_to_sheet([
      ["Item", "Value"],
      ...exportData.selectionSummary
    ]);
    const aggregatedSheet = XLSX.utils.aoa_to_sheet([
      ["Metric", "Value"],
      ...exportData.aggregatedRows
    ]);
    const samplesSheet = XLSX.utils.aoa_to_sheet([
      exportData.sampleHeaders,
      ...exportData.sampleRows
    ]);

    XLSX.utils.book_append_sheet(workbook, filtersSheet, "Filters");
    XLSX.utils.book_append_sheet(workbook, selectionSheet, "Selection");
    XLSX.utils.book_append_sheet(workbook, aggregatedSheet, "Aggregated Stats");
    XLSX.utils.book_append_sheet(workbook, samplesSheet, "Samples");

    const workbookData = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([workbookData], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
    const fileName = buildExportFileName("thermoimaging_hot_area_export", "xlsx");
    downloadBlob(blob, fileName);
  };

  const loadSyncFolderOptions = async () => {
    if (!mountPoint || !window.api?.listNasDir) {
      setSyncFolderOptions([]);
      return;
    }
    setSyncFolderLoading(true);
    try {
      const dataRoot = joinNasPath(mountPoint, NAS_THERMO_INPUT_DIR);
      const listing = await window.api.listNasDir(dataRoot);
      if (!listing.ok || !listing.entries) {
        setSyncFolderOptions([]);
        return;
      }
      const folders = listing.entries
        .filter((entry) => entry.isDir && entry.name !== "output")
        .map((entry) => entry.name)
        .sort();
      setSyncFolderOptions(folders);
      if (syncFolderFilter && !folders.includes(syncFolderFilter)) {
        setSyncFolderFilter("");
      }
    } finally {
      setSyncFolderLoading(false);
    }
  };

  const handleOpenSync = () => {
    if (isShowMode) return;
    setSyncOpen(true);
    setSyncError(null);
  };

  useEffect(() => {
    if (!syncOpen) return;
    loadSyncFolderOptions();
  }, [syncOpen, mountPoint]);

  const handleCloseSync = () => {
    if (syncRunning) return;
    setSyncOpen(false);
  };

  useEscapeStack(syncOpen && !syncRunning, handleCloseSync);
  useEscapeStack(prodStepWarningOpen, () => setProdStepWarningOpen(false));
  useEscapeStack(heightWizardOpen, () => setHeightWizardOpen(false));

  const handleToggleSyncPause = () => {
    if (!syncRunning) return;
    const next = !syncControlRef.current.pause;
    syncControlRef.current.pause = next;
    setSyncPaused(next);
    appendSyncLog(next ? "Sync paused." : "Sync resumed.");
  };

  const handleStopSync = () => {
    if (!syncRunning || syncStopRequested) return;
    syncControlRef.current.stop = true;
    syncControlRef.current.pause = false;
    setSyncStopRequested(true);
    setSyncPaused(false);
    setSyncError(null);
    syncLogSuppressedRef.current = true;
    setSyncLogs([]);
    setSyncProgress(buildInitialSyncProgress());
  };

  const handleClearDryRunOutputs = async () => {
    if (!mountPoint) {
      appendSyncLog("Dry run cleanup skipped: NAS is not mounted.");
      return;
    }
    if (!window.api?.deletePath) {
      appendSyncLog("Dry run cleanup requires the desktop runtime.");
      return;
    }
    const dryRunRoot = joinNasPath(mountPoint, NAS_THERMO_OUTPUT_DIR, "_dry_run");
    const resp = await window.api.deletePath(dryRunRoot);
    if (!resp.ok) {
      appendSyncLog(`Dry run cleanup failed: ${resp.message || "unknown error"}`);
      return;
    }
    appendSyncLog(`Dry run cleanup: removed ${dryRunRoot}.`);
    lastDryRunDirRef.current = null;
  };

  const handleStartSync = async () => {
    if (syncRunning) return;
    setSyncRunning(true);
    setSyncError(null);
    setSyncPaused(false);
    setSyncStopRequested(false);
    syncControlRef.current = { pause: false, stop: false };
    syncLogSuppressedRef.current = false;
    setSyncProgress(buildInitialSyncProgress());
    appendSyncLog("Sync started.", true);
    const log = (message: string, tone?: LogTone) => appendSyncLog(message, false, tone);
    const dryRun = syncDryRun;
    const postgrestUrl = getPostgrestUrl();

    log(dryRun ? "Mode: dry run (no DB writes or output moves)." : "Mode: live sync.");

    if (!mountPoint) {
      log("NAS is not mounted. Mount the NAS and try again.");
      setSyncRunning(false);
      return;
    }
    if (!window.api?.listNasDir || !window.api?.processThermoFile || !window.api?.movePath) {
      log("Sync requires the desktop runtime.");
      setSyncRunning(false);
      return;
    }

    try {
      log("Loading existing thermoimaging records...");
      const existing = await fetchThermoFileIndex();
      const existingKeys = new Set(
        existing.map((row) => `${row.folder_name}/${row.filename}`)
      );
      log(`Database index loaded (${existingKeys.size} files).`);

      let prodStepConfig: ReturnType<typeof buildProductionStepConfigFromMapping> | null = null;
      try {
        log("Loading production step mapping...");
        const mappingRows = await fetchProductionStepMapping();
        if (mappingRows.length) {
          prodStepConfig = buildProductionStepConfigFromMapping(mappingRows);
          const allowedCount = mappingRows.filter((row) => row.allowed).length;
          log(
            `Production step mapping loaded (${mappingRows.length} steps, ${allowedCount} allowed).`,
            "success"
          );
        } else {
          log("Production step mapping is empty; using default config.", "warn");
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "unknown error";
        log(`Production step mapping load failed; using default config. ${message}`, "warn");
      }

      const dataRoot = joinNasPath(mountPoint, NAS_THERMO_INPUT_DIR);
      log(`Scanning NAS root: ${dataRoot}`);
      const rootListing = await window.api.listNasDir(dataRoot);
      if (!rootListing.ok || !rootListing.entries) {
        throw new Error(rootListing.message || "Failed to list NAS input directory.");
      }

      let folders = rootListing.entries
        .filter((entry) => entry.isDir && entry.name !== "output")
        .map((entry) => entry.name)
        .sort();
      const selectedFolder = syncFolderFilter.trim();
      if (selectedFolder) {
        if (!folders.includes(selectedFolder)) {
          log(`Selected folder not found in NAS input: ${selectedFolder}`, "error");
          return;
        }
        folders = [selectedFolder];
        log(`Folder filter active: ${selectedFolder}`);
      }
      if (!syncControlRef.current.stop) {
        setSyncProgress((prev) => ({ ...prev, foldersTotal: folders.length }));
      }
      if (!folders.length) {
        log("No folders found in NAS input directory.");
      }

      let stoppedEarly = false;
      for (const folder of folders) {
        if (!(await waitForSyncReady())) {
          stoppedEarly = true;
          break;
        }
        const folderPath = joinNasPath(dataRoot, folder);
        log(`Scanning folder ${folder}...`);
        const folderListing = await window.api.listNasDir(folderPath);
        if (!folderListing.ok || !folderListing.entries) {
          log(`Failed to list ${folder}: ${folderListing.message || "unknown error"}`);
          continue;
        }

        const csvFiles = folderListing.entries
          .filter((entry) => entry.isFile && entry.name.toLowerCase().endsWith(".csv"))
          .map((entry) => entry.name)
          .sort();

        if (!csvFiles.length) {
          log(`No CSV files found in ${folder}.`);
          continue;
        }

        const skippedFiles = csvFiles.filter((file) => existingKeys.has(`${folder}/${file}`));
        const newFiles = csvFiles.filter((file) => !existingKeys.has(`${folder}/${file}`));
        if (dryRun) {
          if (skippedFiles.length) {
            const sample = skippedFiles.slice(0, 3).join(", ");
            log(
              `Filter check: ${skippedFiles.length} existing file(s) skipped in ${folder}.` +
                (sample ? ` Example: ${sample}` : "")
            );
          }
          const overlap = newFiles.filter((file) => existingKeys.has(`${folder}/${file}`));
          if (overlap.length) {
            log(`Filter check FAILED: ${overlap.length} file(s) already in DB.`);
          } else {
            log("Filter check: OK (folder + filename).");
          }
        }
        const prodStepMatches = new Map<
          string,
          { step: string; score: number; allowed: boolean }
        >();
        const prodStepUnmatched: string[] = [];
        const prodStepDisallowed: Array<{ file: string; step: string; score: number }> = [];
        const matchedFiles = newFiles.filter((file) => {
          const match = matchProductionStep(
            file,
            prodStepConfig ? { productionSteps: prodStepConfig } : undefined
          );
          if (!match) {
            prodStepUnmatched.push(file);
            return false;
          }
          if (!match.allowed) {
            prodStepDisallowed.push({ file, step: match.step, score: match.score });
            return false;
          }
          prodStepMatches.set(file, match);
          return true;
        });
        if (prodStepUnmatched.length) {
          const sample = prodStepUnmatched.slice(0, 3).join(", ");
          log(
            `Prod step filter: skipped ${prodStepUnmatched.length} file(s) with no match.` +
              (sample ? ` Example: ${sample}` : "")
          );
        }
        if (prodStepDisallowed.length) {
          const sample = prodStepDisallowed
            .slice(0, 3)
            .map((entry) => `${entry.file} -> ${entry.step}`)
            .join(", ");
          log(
            `Prod step filter: skipped ${prodStepDisallowed.length} file(s) with disallowed steps.` +
              (sample ? ` Example: ${sample}` : "")
          );
        }
        log(`${matchedFiles.length} new file(s) in ${folder}.`);
        if (!syncControlRef.current.stop) {
          setSyncProgress((prev) => ({
            ...prev,
            currentFolder: folder,
            filesTotal: matchedFiles.length,
            filesDone: 0,
            totalFiles: prev.totalFiles + matchedFiles.length
          }));
        }

        for (const fileName of matchedFiles) {
          if (!(await waitForSyncReady())) {
            stoppedEarly = true;
            break;
          }
          const inputPath = joinNasPath(folderPath, fileName);
          const fileStem = fileName.replace(/\\.csv$/i, "");
          const stagingDir = joinNasPath(
            mountPoint,
            NAS_THERMO_OUTPUT_DIR,
            dryRun ? "_dry_run" : "_staging",
            folder,
            fileStem
          );

          let created: ThermoImage | null = null;
          try {
            const fileStartedAt = Date.now();
            if (dryRun) {
              log(`Dry run output folder: ${stagingDir}`);
            }
            log(`Reading metadata for ${folder}/${fileName}...`);
            const sampleResp = await window.api.processThermoFile({
              inputPath,
              sampleOnly: true,
              postgrestUrl
            });
            if (!sampleResp.ok || !sampleResp.result?.sample) {
              log(`Metadata read failed for ${fileName}: ${sampleResp.message || "unknown error"}`);
              continue;
            }

            const sample = normalizeSamplePayload(sampleResp.result.sample, folder, fileName);
            const prodStepMatch = prodStepMatches.get(fileName);
            if (prodStepMatch && prodStepMatch.step !== sample.prod_step) {
              log(
                `Prod step mismatch for ${fileName}: match=${prodStepMatch.step} (score ${prodStepMatch.score.toFixed(
                  2
                )}), sample=${sample.prod_step}.`
              );
            }
            appendSyncLog(
              `Detected prod_step for ${fileName}: ${sample.prod_step}`,
              false,
              "prod_step"
            );
            if (!dryRun) {
              log(`Inserting metadata for ${fileName}...`);
              created = await createThermoImage(sample);
            } else {
              log("Dry run: skipping metadata insert.");
            }

            const vftTimestamp =
              typeof created?.timestamp === "string" ? created.timestamp : sample.timestamp;
            log(
              `Resolving VFT settings for ${fileName} (${formatAppDateTime(vftTimestamp, vftTimestamp)})...`
            );
            const vft = await fetchVftSettingsForTimestamp(vftTimestamp);
            if (!vft) {
              log(`No VFT settings found for ${fileName}.`);
              if (created) {
                log("Rolling back metadata insert.");
                await safeDeleteSample(created.id, "missing VFT");
              }
              continue;
            }
            log(
              `Using VFT settings from ${formatAppDateTime(vft.timestamp, "resolved timestamp")}.`
            );
            validateVftTimestamp(sample.timestamp, vft.timestamp);

            log(`Processing ${folder}/${fileName}...`);
            const processStart = Date.now();
            const processResp = await window.api.processThermoFile({
              inputPath,
              outputDir: stagingDir,
              vftSetting: vft as VftSettings,
              postgrestUrl
            });
            const processMs = Date.now() - processStart;

            if (!processResp.ok || !processResp.result) {
              log(`Processing failed for ${fileName}: ${processResp.message || "unknown error"}`);
              if (created) {
                await safeDeleteSample(created.id, "processing failed");
              }
              continue;
            }
            log(`Processing completed in ${(processMs / 1000).toFixed(1)}s.`);

            const outputs = processResp.result.outputs || {};
            const missingKeys = THERMO_OUTPUT_KEYS.filter((key) => !(key in outputs));
            if (missingKeys.length) {
              log(`Output keys missing: ${missingKeys.join(", ")}`);
            } else {
              log("Output keys: OK");
            }
            await validateOutputFiles(stagingDir);

            const rawRows = Array.isArray(processResp.result.data) ? processResp.result.data : [];
            const rows = normalizeDataRows(rawRows);
            const bottleCount = rows.filter((row) => row.bottle).length;
            const centerLineCount = rows.filter((row) => row.center_line).length;
            log(
              `Row check: ${rows.length} rows (bottle=${bottleCount}, center_line=${centerLineCount}).`
            );

            if (dryRun) {
              const previousDryRun = lastDryRunDirRef.current;
              if (previousDryRun && previousDryRun !== stagingDir) {
                await deleteDryRunDir(previousDryRun, "keeping latest dry run output");
              }
              lastDryRunDirRef.current = stagingDir;
              const fileMs = Date.now() - fileStartedAt;
              log("Dry run: skipping data insert and output move.");
              log(`Dry run complete for ${fileName} in ${(fileMs / 1000).toFixed(1)}s.`);
              continue;
            }

            if (!created) {
              log("Missing created sample; skipping data insert.");
              continue;
            }

            log(`Inserting ${rows.length} data rows for sample ${created.id}...`);
            try {
              await insertThermoData(created.id, rows);
            } catch (err) {
              const message = err instanceof Error ? err.message : "insert failed";
              log(`Data insert failed for ${fileName}: ${message}`);
              await safeDeleteSample(created.id, "data insert failed");
              continue;
            }

            const finalOutputDir = joinNasPath(
              mountPoint,
              NAS_THERMO_OUTPUT_DIR,
              String(created.id)
            );
            const moveResult = await window.api.movePath(stagingDir, finalOutputDir);
            if (!moveResult.ok) {
              log(`Output move failed for ${fileName}: ${moveResult.message || "unknown error"}`);
            } else {
              log(`Saved outputs to ${finalOutputDir}.`);
            }

            existingKeys.add(`${folder}/${fileName}`);
            const fileMs = Date.now() - fileStartedAt;
            log(`Sync complete for ${fileName} in ${(fileMs / 1000).toFixed(1)}s.`);
          } catch (err) {
            const message = err instanceof Error ? err.message : "sync failed";
            log(`Sync failed for ${fileName}: ${message}`);
            if (created) {
              await safeDeleteSample(created.id, "unexpected error");
            }
          } finally {
            if (!syncControlRef.current.stop) {
              setSyncProgress((prev) => ({
                ...prev,
                filesDone: Math.min(prev.filesDone + 1, prev.filesTotal),
                totalFilesDone: prev.totalFilesDone + 1
              }));
            }
          }
        }
        if (!stoppedEarly) {
          if (!syncControlRef.current.stop) {
            setSyncProgress((prev) => ({
              ...prev,
              foldersDone: Math.min(prev.foldersDone + 1, prev.foldersTotal),
              filesTotal: 0,
              filesDone: 0
            }));
          }
        }
        if (stoppedEarly) break;
      }

      clearCache();
      await loadMetadata();
      if (!stoppedEarly && !syncControlRef.current.stop) {
        log("Sync complete.");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Sync failed";
      if (!syncControlRef.current.stop) {
        setSyncError(message);
        log(`Error: ${message}`);
      }
    } finally {
      const wasStopped = syncControlRef.current.stop;
      setSyncRunning(false);
      setSyncPaused(false);
      setSyncStopRequested(false);
      syncControlRef.current = { pause: false, stop: false };
      setSyncProgress((prev) => ({ ...prev, currentFolder: "" }));
      if (wasStopped) {
        appendSyncLog("Sync stopped.", true, "warn", true);
      }
      syncLogSuppressedRef.current = false;
    }
  };

  const renderProgressRow = (label: string, value: number, total: number, accent: string) => {
    const percent = total ? clampPercent((value / total) * 100) : 0;
    return (
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 12,
            color: "var(--text-muted)",
            marginBottom: 4
          }}
        >
          <span>{label}</span>
          <span>{total ? `${value}/${total}` : "-"}</span>
        </div>
        <div
          style={{
            height: 8,
            borderRadius: 999,
            background: "rgba(255, 255, 255, 0.08)",
            overflow: "hidden",
            border: "1px solid #2a3f5f"
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${percent}%`,
              background: accent,
              transition: "width 0.2s ease"
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="grid" style={{ gap: 12 }}>
      <div className={needsLogin ? "overlay-blocker" : ""}>
        <div className="grid" style={{ gap: 16 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap"
            }}
          >
            <div className="label">
              {isShowMode
                ? "Choose a curated thermo preset to load the published analysis."
                : "Select samples in the table, then click Analyze to load gallery + center line profiles and detect anomalies."}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {!isShowMode && (
                <button
                  className="button"
                  onClick={() => handleAnalyze()}
                  disabled={anomalyLoading || selectedIds.size === 0}
                >
                  {anomalyLoading
                    ? "Analyzing..."
                    : anomalyDurationMs != null
                    ? `Analyze (Took ${(anomalyDurationMs / 1000).toFixed(1)}s)`
                    : "Analyze"}
                </button>
              )}
              <button className="button secondary" onClick={handleResetAll}>
                Reset View
              </button>
              {!isShowMode && (
                <button
                  className="button secondary"
                  onClick={handleOpenSync}
                  disabled={syncRunning || needsLogin}
                >
                  Sync NAS
                </button>
              )}
            </div>
          </div>
          {isShowMode && (
            <div className="card" style={{ background: "var(--surface-elevated)" }}>
              <div className="label" style={{ marginBottom: 8 }}>
                Thermo Preset
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <select
                  className="select mobile-full"
                  value={showSelectedPresetId ?? ""}
                  onChange={(event) => {
                    const raw = Number(event.target.value);
                    setShowSelectedPresetId(Number.isFinite(raw) && raw > 0 ? raw : null);
                    setShowPresetStatus(null);
                    setAnomalyError(null);
                  }}
                  disabled={showPresetBusy || loading || !showPresets.length}
                >
                  <option value="">
                    {showPresetLoading ? "Loading presets..." : "Select preset"}
                  </option>
                  {showPresets.map((preset) => (
                    <option key={preset.id} value={preset.id}>
                      #{preset.id} {preset.name} ({preset.sample_id_array.length} samples)
                    </option>
                  ))}
                </select>
                <button
                  className="button"
                  onClick={() => {
                    void handleLoadShowPreset(showSelectedPresetId);
                  }}
                  disabled={showPresetBusy || loading || showSelectedPresetId == null}
                >
                  {showPresetHydrating ? "Loading..." : "Show Preset"}
                </button>
                <button
                  className="button secondary"
                  onClick={() => {
                    void loadShowPresets();
                  }}
                  disabled={showPresetLoading}
                >
                  {showPresetLoading ? "Refreshing..." : "Refresh Presets"}
                </button>
              </div>
              {showPresetStatus && (
                <div className="status-error" style={{ marginTop: 8 }}>
                  {showPresetStatus}
                </div>
              )}
            </div>
          )}
          {isShowMode && showLoadedPresetId != null && (
            <FilterPanel
              filters={draftFilters}
              onChange={setDraftFilters}
              onApply={handleApplyFilters}
              onClear={handleClearFilters}
              prodSteps={prodSteps}
              trialOptions={showTrialOptions}
              readOnly
              readOnlyValues={showReadOnlyFilterLabels}
            />
          )}
          {!isShowMode && (
            <>
              <FilterPanel
                filters={draftFilters}
                onChange={setDraftFilters}
                onApply={handleApplyFilters}
                onClear={handleClearFilters}
                prodSteps={prodSteps}
                trialOptions={trialOptions}
              />

              <ThermoTable
                data={filtered}
                selectedIds={selectedIds}
                onToggle={handleSelectToggle}
                onSelectAll={handleSelectAll}
                page={page}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
                sort={sort}
                onSortChange={setSort}
                searchTerm={searchTerm}
                onSearchChange={handleSearchChange}
              />
            </>
          )}

          {showHasLoadedPreset ? (
            <>
              <div className="analysis-grid">
                <CenterLineChart
                  data={aggregated.centerLine}
                  title="Average Center Line (multi-selection)"
                  mode="multi"
                  vftCoeffs={analyzedVft}
                  loading={detailLoading}
                  xAxisKey={usesProjectedHeight ? "height_mm" : "y"}
                  xAxisLabel={usesProjectedHeight ? "Projected Height (mm)" : undefined}
                  actions={
                    !isShowMode ? (
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        {currentHeightReference && (
                          <span className="pill">
                            Height ref: {currentHeightReference.referenceHeightMm.toFixed(0)} mm
                          </span>
                        )}
                        <button
                          className="button secondary"
                          onClick={() => setHeightWizardOpen(true)}
                          disabled={!canOpenHeightWizard}
                          title={
                            canOpenHeightWizard
                              ? "Set or update the common height projection for this multiple analysis"
                              : "Analyze the full selection and wait for center line details to load first"
                          }
                        >
                          {currentHeightReference ? "Edit Height" : "Set Height"}
                        </button>
                      </div>
                    ) : null
                  }
                />
                <StatsPanel
              title="Aggregated Stats"
              stats={aggregated.stats}
              actionsMode="active"
              tabs={[
                {
                  label: "Center Line",
                  stats: aggregated.stats,
                  actions: isShowMode
                    ? []
                    : [
                        {
                          label: "Export",
                          onClick: handleExportCenterLineHtml,
                          disabled: analyzedImages.length === 0,
                          title: "Export center line stats as HTML",
                          className: "export-action"
                        },
                        {
                          label: "Export XLSX",
                          onClick: handleExportCenterLineXlsx,
                          disabled: analyzedImages.length === 0,
                          title: "Export center line stats as XLSX",
                          className: "export-xlsx"
                        }
                      ]
                },
                {
                  label: "Gob | Bottle",
                  stats: aggregated.bottleStats,
                  actions: isShowMode
                    ? []
                    : [
                        {
                          label: "Export",
                          onClick: handleExportBottleStatsHtml,
                          disabled: analyzedImages.length === 0,
                          title: "Export hot area stats as HTML",
                          className: "export-action"
                        },
                        {
                          label: "Export XLSX",
                          onClick: handleExportBottleStatsXlsx,
                          disabled: analyzedImages.length === 0,
                          title: "Export hot area stats as XLSX",
                          className: "export-xlsx"
                        }
                      ]
                }
              ]}
            />
          </div>

          <Gallery
            images={analyzedImages}
            anomalies={anomalyImages.map((img) => img.id)}
            nasMountPath={mountPoint}
            onUnselect={(id) => handleRemoveFromAnalysis(id)}
            onViewDetail={(image) => setDetailModalImage(image)}
            anomalyLoading={anomalyLoading}
            anomalyError={anomalyError}
            page={galleryPage}
            pageSize={8}
            onPageChange={setGalleryPage}
            anomalyPage={anomalyGalleryPage}
            onAnomalyPageChange={setAnomalyGalleryPage}
          />

          {!isShowMode && (
            <AnalysisPanel
              selectedIds={Array.from(analyzedIds)}
              meanCenterLine={aggregated.centerLine}
              stats={aggregated.stats}
              bottleStats={aggregated.bottleStats}
              heightReferenceSampleId={currentHeightReference?.referenceSampleId ?? null}
              heightReferenceLine={currentHeightReference?.referenceLine ?? null}
              heightReferenceHeightMm={currentHeightReference?.referenceHeightMm ?? null}
              refreshNonce={analysisRefreshKey}
              onRestore={handleRestoreAnalysis}
            />
          )}
            </>
          ) : (
            <div className="card" style={{ textAlign: "center", color: "var(--text-faint)" }}>
              Select a thermo preset, then click Show Preset to load the curated analysis.
            </div>
          )}
        </div>
      </div>

      <DetailModal
        open={!!detailModalImage}
        image={detailModalImage}
        detail={detailForModal}
        detailLoading={detailModalLoading}
        detailError={detailModalError}
        vftCoeffs={detailModalImage?.vftCoeffs}
        onClose={() => setDetailModalImage(null)}
      />

      <ThermoHeightWizardModal
        open={heightWizardOpen}
        images={analyzedImages}
        details={details}
        nasMountPath={mountPoint}
        onClose={() => setHeightWizardOpen(false)}
        onApply={handleApplyHeightCalibration}
      />

      {loading && <div className="card">Loading metadata from PostgREST…</div>}
      {loadError && (
        <div className="card" style={{ border: "1px solid #ff9f9f" }}>
          <div className="status-error">Metadata load error: {loadError}</div>
          <div className="label">Thermoimaging rows may appear empty if the PostgREST view/relationship is not configured for active_trials embedding.</div>
        </div>
      )}
      {nasError && (
        <div className="card" style={{ border: "1px solid #ff9f9f" }}>
          <div className="status-error">NAS error: {nasError}</div>
          {!isShowMode && (
            <div className="label">Update credentials in Settings to retry mounting.</div>
          )}
        </div>
      )}

      <NasLoginModal
        open={needsLogin}
        credentials={nasCreds}
        onChange={(cfg) => {
          setNasCreds(cfg);
          setConfig(cfg);
        }}
        onSubmit={() => triggerMount(nasCreds)}
        status={nasStatus}
        error={nasError}
      />

      {!isShowMode && syncOpen && (
        <div className="modal-backdrop" style={{ zIndex: 2400 }}>
          <div className="modal" style={{ maxWidth: 900 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <h3 className="section-title" style={{ margin: 0 }}>
                Thermoimaging Sync
              </h3>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                {syncPaused && <span className="pill">Paused</span>}
                {syncStopRequested && <span className="pill">Stopping...</span>}
                <span className="pill">NAS: {mountPoint || "not mounted"}</span>
              </div>
            </div>
            <div className="label" style={{ marginTop: 8 }}>
              Scans NAS folders, processes new CSV files, and inserts results into the database.
            </div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 10,
                fontSize: 13,
                color: "var(--text-muted)"
              }}
            >
              <input
                type="checkbox"
                checked={syncDryRun}
                onChange={(event) => setSyncDryRun(event.target.checked)}
                disabled={syncRunning}
              />
              Dry run (no DB writes or output moves)
            </label>
            <div style={{ display: "grid", gap: 6, marginTop: 10 }}>
              <div className="label">Folder selection</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <select
                  className="select mobile-full"
                  value={syncFolderFilter}
                  onChange={(event) => setSyncFolderFilter(event.target.value)}
                  disabled={syncRunning || syncFolderLoading || needsLogin}
                >
                  <option value="">All folders</option>
                  {syncFolderOptions.map((folder) => (
                    <option key={folder} value={folder}>
                      {folder}
                    </option>
                  ))}
                </select>
                <button
                  className="button secondary"
                  onClick={loadSyncFolderOptions}
                  disabled={syncRunning || syncFolderLoading || needsLogin}
                >
                  {syncFolderLoading ? "Loading..." : "Refresh"}
                </button>
                {!syncFolderLoading && syncFolderOptions.length === 0 && (
                  <span className="label" style={{ color: "var(--text-faint)" }}>
                    No folders found
                  </span>
                )}
              </div>
              <div className="label" style={{ color: "var(--text-faint)" }}>
                If a folder is selected, only that folder will be synced.
              </div>
            </div>

            {(syncRunning || syncProgress.foldersTotal > 0) && (
              <div className="card" style={{ background: "var(--surface-elevated)", marginTop: 12 }}>
                <div className="label" style={{ marginBottom: 8 }}>
                  Sync Progress
                </div>
                <div style={{ display: "grid", gap: 10 }}>
                  {renderProgressRow(
                    "Folders",
                    syncProgress.foldersDone,
                    syncProgress.foldersTotal,
                    "var(--accent-one)"
                  )}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 12,
                      color: "var(--text-muted)"
                    }}
                  >
                    <span>Current folder</span>
                    <span style={{ color: "var(--text-primary)" }}>
                      {syncProgress.currentFolder || "-"}
                    </span>
                  </div>
                  {renderProgressRow(
                    "Files in folder",
                    syncProgress.filesDone,
                    syncProgress.filesTotal,
                    "var(--accent-two)"
                  )}
                  {renderProgressRow(
                    "Total files",
                    syncProgress.totalFilesDone,
                    syncProgress.totalFiles,
                    "var(--accent-three)"
                  )}
                </div>
              </div>
            )}

            <div
              className="card"
              style={{
                background: "var(--surface-elevated)",
                marginTop: 12,
                maxHeight: 280,
                overflow: "auto",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace",
                fontSize: 12
              }}
            >
              {syncLogs.length ? (
                <div style={{ display: "grid", gap: 4 }}>
                  {syncLogs.map((entry, index) => (
                    <div
                      key={`${index}-${entry.message}`}
                      style={{
                        color: LOG_TONE_COLORS[entry.tone],
                        whiteSpace: "pre-wrap"
                      }}
                    >
                      {entry.message}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: "var(--text-faint)" }}>No logs yet.</div>
              )}
            </div>

            {syncError && (
              <div className="status-error" style={{ marginTop: 10 }}>
                {syncError}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <button
                className="button secondary"
                onClick={() => setSyncLogs([])}
                disabled={syncRunning}
              >
                Clear Logs
              </button>
              <button
                className="button secondary"
                onClick={handleToggleSyncPause}
                disabled={!syncRunning}
              >
                {syncPaused ? "Resume" : "Pause"}
              </button>
              <button
                className="button secondary"
                onClick={handleStopSync}
                disabled={!syncRunning || syncStopRequested}
              >
                {syncStopRequested ? "Stopping..." : "Stop Sync"}
              </button>
              <button
                className="button secondary"
                onClick={handleClearDryRunOutputs}
                disabled={syncRunning}
              >
                Clear Dry Run Outputs
              </button>
              <button className="button secondary" onClick={handleCloseSync} disabled={syncRunning}>
                Close
              </button>
              <button
                className="button"
                onClick={handleStartSync}
                disabled={syncRunning || needsLogin}
              >
                {syncRunning ? "Syncing..." : "Start Sync"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isShowMode && showPresetHydrating && (
        <div className="show-loading-overlay">
          <div className="loading-popup" role="status" aria-live="polite">
            <div className="spinner" aria-label="Loading thermo preset" />
            <div>
              <div className="section-title" style={{ marginBottom: 4 }}>
                Loading preset…
              </div>
              <div className="label">Preparing the published thermo analysis.</div>
            </div>
          </div>
        </div>
      )}

      {anomalyLoading && (
        <div className="modal-backdrop" style={{ zIndex: 2500 }}>
          <div className="loading-popup">
            <div className="spinner" aria-label="Detecting anomalies" />
            <div>
              <div className="section-title" style={{ marginBottom: 4 }}>
                Detecting anomalies…
              </div>
              <div className="label">
                Crunching numbers on analyzed samples
                {anomalyStartRef.current
                  ? ` · ${((Date.now() - anomalyStartRef.current) / 1000).toFixed(1)}s`
                  : ""}
              </div>
            </div>
          </div>
        </div>
      )}

      {prodStepWarningOpen && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 420 }}>
            <h3 className="section-title" style={{ marginTop: 0 }}>
              Mixed production steps
            </h3>
            <p style={{ color: "var(--text-primary)", marginTop: 8 }}>
              Your selected samples belong to more than one production step. Are you sure you want to analyze them together?
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
              <button className="button secondary" onClick={() => setProdStepWarningOpen(false)}>
                Cancel
              </button>
              <button
                className="button"
                onClick={() => {
                  setProdStepWarningOpen(false);
                  handleAnalyze(true);
                }}
              >
                Yes, continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
